/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 * Copyright © 2021 ООО АДАПТ info@acfs.spb.ru
 * You may use, distribute and modify this code under the
 * terms of the ООО АДАПТ license
 */

import { FunctionComponent, useCallback, useEffect, useState } from 'react'

import {
  getCountUnreadNotifications,
  getNotifications,
} from '@builddocs/data-management'
import {
  Badge,
  Box,
  FormControlLabel,
  FormGroup,
  Pagination,
  Switch,
  Tooltip,
} from '@mui/material'
import IconButton, { IconButtonProps } from '@mui/material/IconButton'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import {
  usePopupState,
  bindTrigger,
  bindPopover,
} from 'material-ui-popup-state/hooks'
import { useQuery } from 'react-query'

import ListIcon from '../assets/list.svg'

import { NotificationFilter } from './molecules/NotificationFilter'
import { NotificationSort } from './molecules/NotificationSort'
import NotificationList from './organisms/NotificationList'
import ym from 'react-yandex-metrika'

interface OwnProps {
  label?: string
  placement?:
    | 'bottom'
    | 'left'
    | 'right'
    | 'top'
    | 'bottom-end'
    | 'bottom-start'
    | 'left-end'
    | 'left-start'
    | 'right-end'
    | 'right-start'
    | 'top-end'
    | 'top-start'
}

export type NotificationsButtonWithPopoverProps = OwnProps & IconButtonProps

export const NotificationsButtonWithPopover: FunctionComponent<NotificationsButtonWithPopoverProps> =
  ({ label, placement = 'bottom', ...rest }) => {
    const [pageNumber, setPageNumber] = useState<number>(0)
    const [onlyUnread, setOnlyUnread] = useState<boolean | null>(null)
    const [filterParams, setFilterParams] = useState<string[]>([])
    const [sortParam, setSortParam] = useState<string>('DATE')
    const [forWorkOnly, setForWorkOnly] = useState<boolean>(true)
    const popupState = usePopupState({
      variant: 'popover',
      popupId: 'popover-notifications',
    })

    const handleChangeFilters = useCallback(
      (filters: string[]) => {
        setFilterParams(filters)
      },
      [filterParams],
    )

    const handleChangeSortParam = useCallback(
      (newSortParam: string) => {
        setSortParam(newSortParam)
      },
      [sortParam],
    )

    const {
      data: notificationsResponse,
      isFetching,
      refetch,
    } = useQuery(
      [
        'notifications',
        pageNumber,
        onlyUnread,
        filterParams,
        sortParam,
        forWorkOnly,
      ],
      async () =>
        await getNotifications({
          pageNumber,
          pageSize: 5,
          unread: onlyUnread,
          filters: filterParams.join(','),
          sort: sortParam,
          forWorkOnly,
        }),
    )
    const notifications = notificationsResponse?.entityList

    const pageCount = notificationsResponse?.pageCount ?? 1

    const { data: countUnreadNotifications, refetch: refetchCount } = useQuery(
      ['countUnreadNotifications'],
      async () => await getCountUnreadNotifications(),
    )

    const bindPopoverProps = bindPopover(popupState)

    const refetchHandle = (unread?: boolean | null) => {
      unread !== undefined && setOnlyUnread(unread)

      if (pageNumber >= pageCount) setPageNumber(pageCount - 1)
      else if (pageNumber < 0) setPageNumber(0)

      refetchCount()
      refetch()
    }

    useEffect(() => {
      refetch()
    }, [countUnreadNotifications])

    useEffect(() => {
      const timeout = setTimeout(refetchCount, 30000)

      return () => clearTimeout(timeout)
    })

    return (
      <Box>
        <Tooltip placement={placement} title="Поток задач">
          <IconButton {...bindTrigger(popupState)} {...rest} sx={{ p: 1 }}>
            <Badge
              badgeContent={countUnreadNotifications}
              color="primary"
              sx={{ fontSize: 'inherit' }}
            >
              <Box
                alt="Поток задач"
                component="img"
                src={ListIcon}
                sx={{ width: '100%' }}
              />
            </Badge>
          </IconButton>
        </Tooltip>
        <Popover
          {...bindPopoverProps}
          PaperProps={{
            sx: {
              width: '100%',
              maxWidth: { sm: 600, xs: 'calc(100% - 32px)' },
            },
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <HandlerYM popupState={popupState}/>
          <Stack spacing={1} sx={{ width: { sm: 600, xs: '100%' } }}>
            <Stack
              alignItems="center"
              direction="row"
              spacing={3}
              sx={{ px: 2, pt: 2 }}
            >
              <Typography
                component="span"
                fontSize={16}
                fontWeight={500}
                textTransform="uppercase"
                variant="body1"
              >
                Поток задач
              </Typography>
            </Stack>
            <Stack alignItems="flex-start">
              <Stack
                direction="row"
                spacing={2}
                sx={{ px: 2, py: 1, width: '100%' }}
              >
                <NotificationFilter
                  filters={filterParams}
                  onChange={handleChangeFilters}
                />
                <NotificationSort onChange={handleChangeSortParam} />
              </Stack>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={forWorkOnly}
                      onChange={e => setForWorkOnly(e.target.checked)}
                    />
                  }
                  label="Только требующие моих действий"
                  labelPlacement="start"
                />
              </FormGroup>
            </Stack>
            <NotificationList
              isLoading={isFetching}
              lastPage={pageNumber + 1 === pageCount}
              notifications={notifications}
              onlyUnread={onlyUnread}
              refetch={refetchHandle}
              setPageNumber={setPageNumber}
              onClosePopover={bindPopoverProps.onClose}
            />
            {pageCount > 1 && (
              <Pagination
                count={pageCount}
                page={pageNumber + 1}
                size="small"
                sx={{ p: 2, pt: 1 }}
                onChange={(_e, pageNumber) => setPageNumber(pageNumber - 1)}
              />
            )}
          </Stack>
        </Popover>
      </Box>
    )
  }

export default NotificationsButtonWithPopover


const HandlerYM = ({popupState}) => {
  useEffect(() => {
    if(popupState.isOpen){
      ym('reachGoal','open-task-manager')
    }
  }, [popupState])
  return(
    <></>
  )
}
